/**
 * Prefills the calculator from the trainee's REAL data. Three sources, all existing
 * admin endpoints:
 *
 *   getNutritionCalculatorData  → gender / age / height / weight, goal + weekly range
 *   getClientWeightTable        → the latest week's TARGET steps, goal and weekly range
 *                                 (the same numbers the weight page edits — they win,
 *                                 being the most recently set by the trainer)
 *   getCurrentPlan              → the active program: strength sessions per week and the
 *                                 average workout length
 *
 * Anything a source doesn't provide keeps the default, and every source is independently
 * optional — one failing call must not block the calculator.
 */

import moment from "moment";
import {
  defaultFiber,
  mapStepsToLevel,
  toGoalValue,
  toWeeklyRangeValue,
  type Gender,
} from "../../../../utils/nutritionCalculator";
import { defaultCalculatorState, type CalculatorState } from "./calculatorState";
import { getNutritionCalculatorData, getClientWeightTable } from "../../../../services/TraineeService";
import { getCurrentPlan } from "../../../../services/PlanSerice";

/**
 * Which fields actually came from the trainee, as opposed to a default. The calculator no
 * longer labels them in the UI — the trainer edits whatever they want anyway — but the
 * distinction is worth keeping for the server phase.
 */
export type PrefillSources = {
  profile: boolean;
  goal: boolean;
  stepsGoal: boolean;
  program: boolean;
};

export type PrefillResult = {
  state: CalculatorState;
  sources: PrefillSources;
};

type ProgramWorkout = { totalDuration?: number };
type ProgramWeek = { index?: number; workouts?: ProgramWorkout[] };
type ProgramBlock = { weeks?: ProgramWeek[] };
type Program = { blocks?: ProgramBlock[] };

/**
 * Sessions per week + average session length, off the active program.
 * `totalDuration` is in MINUTES (same unit the week-overview stats render).
 * Only workouts that actually logged a duration count towards the average.
 */
export const summariseProgram = (
  program: Program | undefined
): { workouts?: number; workoutDuration?: number } => {
  const weeks = (program?.blocks || []).flatMap((b) => b.weeks || []);
  if (weeks.length === 0) return {};

  // sessions per week: the widest week in the program — a deload week would understate it
  const workoutsPerWeek = Math.max(...weeks.map((w) => (w.workouts || []).length));

  const durations = weeks
    .flatMap((w) => w.workouts || [])
    .map((w) => Number(w.totalDuration) || 0)
    .filter((d) => d > 0);

  const average = durations.length
    ? Math.round(durations.reduce((a, d) => a + d, 0) / durations.length)
    : undefined;

  return {
    workouts: workoutsPerWeek > 0 ? workoutsPerWeek : undefined,
    // snap onto the dropdown's buckets
    workoutDuration: average ? nearestDuration(average) : undefined,
  };
};

const DURATION_BUCKETS = [30, 45, 60, 75, 90];

export const nearestDuration = (minutes: number): number =>
  DURATION_BUCKETS.reduce((best, b) => (Math.abs(b - minutes) < Math.abs(best - minutes) ? b : best));

/** The most recent week that actually carries goals. */
const latestWeekWithGoals = (weeks: Array<Record<string, unknown>> = []) =>
  [...weeks]
    .sort((a, b) => (Number(b.weekDate) || 0) - (Number(a.weekDate) || 0))
    .find((w) => w.goal || w.weeklyRange || w.stepsGoal);

export const buildTraineePrefill = async (traineeId: string): Promise<PrefillResult> => {
  const state = defaultCalculatorState();
  const sources: PrefillSources = { profile: false, goal: false, stepsGoal: false, program: false };

  const [calcData, weightTable, program] = await Promise.all([
    getNutritionCalculatorData(traineeId).catch(() => null),
    // the weight page's own window: the last ~12 weeks
    getClientWeightTable(traineeId, moment().subtract(12, "weeks"), moment()).catch(() => null),
    getCurrentPlan(traineeId).catch(() => null),
  ]);

  // ── profile + goal, from the nutrition calculator endpoint ──
  const data = calcData?.data;
  if (data) {
    sources.profile = true;
    const gender: Gender = String(data.gender || "").toUpperCase() === "FEMALE" ? "female" : "male";
    state.gender = gender;
    state.fiber = defaultFiber(gender);
    if (data.age) state.age = data.age;
    if (data.height) state.height = data.height;
    if (data.lastWeight) state.weight = data.lastWeight;
    if (data.numOfDaysTraining != null) state.workouts = data.numOfDaysTraining;
    if (data.weeklyAverageSteps) state.steps = mapStepsToLevel(data.weeklyAverageSteps);
    if (data.goal) {
      sources.goal = true;
      state.goal = toGoalValue(data.goal);
      state.weeklyRange = toWeeklyRangeValue(data.weeklyRange, state.goal);
    }
  }

  // ── target steps + goal, from the weight table (what the trainer set most recently) ──
  const week = latestWeekWithGoals(weightTable?.data?.weeks);
  if (week) {
    if (week.stepsGoal) {
      sources.stepsGoal = true;
      // activity level follows the TARGET steps, not the historical average
      state.steps = mapStepsToLevel(Number(week.stepsGoal));
    }
    if (week.goal) {
      sources.goal = true;
      state.goal = toGoalValue(String(week.goal));
      state.weeklyRange = toWeeklyRangeValue(
        week.weeklyRange ? String(week.weeklyRange) : undefined,
        state.goal
      );
    }
  }

  // ── sessions per week + average length, from the active program ──
  const summary = summariseProgram(program?.data);
  if (summary.workouts != null || summary.workoutDuration != null) {
    sources.program = true;
    if (summary.workouts != null) state.workouts = summary.workouts;
    if (summary.workoutDuration != null) state.workoutDuration = summary.workoutDuration;
  }

  return { state, sources };
};
